import {
  BALANCE,
  FACTIONS,
  MAP_SPACING,
  RICHNESS,
  MAX_PLAYERS_PER_FACTION,
  STAR_CLASSES,
  orbitAu,
  zoneAt,
  type FactionId,
  type PlanetKind,
  type PlayerCount,
  type RichnessId,
  type StarClassId,
  type ZoneId,
} from '@sd/content';
import {
  ORIGIN,
  hexAngle,
  hexDistance,
  hexKey,
  hexOnRingAt,
  hexRing,
  hexSpiral,
  type Hex,
} from './hex';
import { Rng } from './rng';

/** Star-system generation (GDD §3): zones, orbits, home planets, dust clouds. */

export interface PlayerSetup {
  readonly id: string;
  readonly faction: FactionId;
  /** Teammates share a team number (#137); none = everyone for themselves. */
  readonly team?: number;
}

export interface Orbit {
  readonly index: number;
  readonly au: number;
  readonly zone: ZoneId;
  /** Hex distance from the star centre. */
  readonly ringRadius: number;
}

export type { PlanetKind };

export interface Planet {
  readonly id: string;
  readonly orbit: number;
  readonly hex: Hex;
  readonly kind: PlanetKind;
  /** Player id when this is a capital, otherwise null (neutral body for outposts). */
  readonly homeOf: string | null;
}

export type CloudLevel = 1 | 2 | 3;

export interface DustCloud {
  readonly id: string;
  readonly level: CloudLevel;
  readonly center: Hex;
  readonly hexes: readonly Hex[];
}

/** L4 or L5: 60° ahead of or behind a gas giant on its orbit (GDD §3), a home for trojans. */
export interface LagrangePoint {
  readonly id: string;
  readonly hex: Hex;
  /** The gas giant it belongs to. */
  readonly giantId: string;
  readonly side: 'L4' | 'L5';
}

export interface StarSystem {
  readonly seed: string;
  readonly starClass: StarClassId;
  readonly mapRadius: number;
  readonly starHexes: readonly Hex[];
  readonly coronaHexes: readonly Hex[];
  readonly orbits: readonly Orbit[];
  readonly planets: readonly Planet[];
  readonly clouds: readonly DustCloud[];
  readonly lagrangePoints: readonly LagrangePoint[];
  /** Which generation attempt passed the fairness check (for diagnostics). */
  readonly attempt: number;
}

export interface GenerateOptions {
  readonly seed: string;
  readonly players: readonly PlayerSetup[];
  readonly starClass?: StarClassId;
  /** How rich the system is (#98); default «balanced». */
  readonly richness?: RichnessId;
}

export class MapGenError extends Error {
  override name = 'MapGenError';
}

/** Cloud footprint radius by level: 1 → 1 hex, 2 → 7 hexes, 3 → 19 hexes (GDD §7). */
const CLOUD_RADIUS: Readonly<Record<CloudLevel, number>> = { 1: 0, 2: 1, 3: 2 };

export function validatePlayers(players: readonly PlayerSetup[]): void {
  if (players.length < 2 || players.length > 4) {
    throw new MapGenError(`A match needs 2–4 players, got ${players.length}`);
  }
  const ids = new Set(players.map((p) => p.id));
  if (ids.size !== players.length) throw new MapGenError('Player ids must be unique');
  const perFaction = new Map<FactionId, number>();
  for (const p of players) {
    if (!(p.faction in FACTIONS)) throw new MapGenError(`Unknown faction ${p.faction}`);
    perFaction.set(p.faction, (perFaction.get(p.faction) ?? 0) + 1);
  }
  for (const [faction, count] of perFaction) {
    if (count > MAX_PLAYERS_PER_FACTION) {
      throw new MapGenError(
        `At most ${MAX_PLAYERS_PER_FACTION} players per faction (${faction}: ${count})`,
      );
    }
  }
}

export function buildOrbits(starClass: StarClassId, mapRadius: number): Orbit[] {
  const star = STAR_CLASSES[starClass];
  const first = star.radiusHexes + 1; // the ring right after the corona
  const spacing = (mapRadius - first) / BALANCE.orbitCount;
  const orbits: Orbit[] = [];
  let previous = first - 1;
  for (let index = 0; index < BALANCE.orbitCount; index++) {
    const ideal = first + Math.round(spacing * (index + 0.5));
    const ringRadius = Math.max(ideal, previous + 1);
    if (ringRadius >= mapRadius) break;
    const au = orbitAu(index);
    orbits.push({ index, au, zone: zoneAt(au, star.luminositySun), ringRadius });
    previous = ringRadius;
  }
  return orbits;
}

function planetKindFor(zone: ZoneId, isHome: boolean, rng: Rng): PlanetKind {
  switch (zone) {
    case 'corona':
    case 'hot':
      return isHome ? 'desert' : rng.pick(['lava', 'desert'] as const);
    case 'habitable':
      return 'terran';
    case 'belt':
      return isHome ? 'desert' : 'asteroids';
    case 'giants':
      return isHome ? 'ice' : 'gasGiant';
    case 'outer':
      return 'ice';
  }
}

interface HomeSlot {
  player: PlayerSetup;
  orbit: Orbit;
  angle: number;
}

/**
 * Home placement rules (GDD §3):
 * - home planet sits on an orbit of the faction's zone (Skyborn: any free orbit);
 * - two players of one faction share an orbit at opposite points;
 * - otherwise capitals are spread evenly by angle with ±15° jitter.
 */
function placeHomes(
  players: readonly PlayerSetup[],
  orbits: readonly Orbit[],
  rng: Rng,
): HomeSlot[] {
  const base = rng.range(0, 2 * Math.PI);
  const jitter = () => rng.range(-Math.PI / 12, Math.PI / 12);

  const groups = new Map<FactionId, PlayerSetup[]>();
  for (const p of players) groups.set(p.faction, [...(groups.get(p.faction) ?? []), p]);
  const pairs = [...groups.values()].filter((g) => g.length === 2);
  const singles = [...groups.values()].filter((g) => g.length === 1).flat();

  // Angle slots: pairs take opposite ends first; singles fill the gaps.
  const slots: { player: PlayerSetup; angle: number }[] = [];
  if (pairs.length > 0) {
    pairs.forEach((pair, i) => {
      const a = base + (i * Math.PI) / 2;
      slots.push({ player: pair[0] as PlayerSetup, angle: a });
      slots.push({ player: pair[1] as PlayerSetup, angle: a + Math.PI });
    });
    const free = [base + Math.PI / 2, base + (3 * Math.PI) / 2].slice(pairs.length - 1);
    singles.forEach((player, i) => slots.push({ player, angle: (free[i] ?? base) + jitter() }));
  } else {
    singles.forEach((player, i) =>
      slots.push({ player, angle: base + (i * 2 * Math.PI) / singles.length + jitter() }),
    );
  }

  const usedOrbits = new Set<number>();
  const orbitFor = new Map<FactionId, Orbit>();
  // Factions with a fixed home zone choose first, so free-orbit factions can't block them.
  const ordered = [...slots].sort(
    (a, b) =>
      Number(FACTIONS[a.player.faction].homeZone === null) -
      Number(FACTIONS[b.player.faction].homeZone === null),
  );
  const result: HomeSlot[] = [];
  for (const { player, angle } of ordered) {
    let orbit = orbitFor.get(player.faction);
    if (!orbit) {
      const zone = FACTIONS[player.faction].homeZone;
      const candidates = orbits.filter(
        (o) => (zone === null || o.zone === zone) && !usedOrbits.has(o.index),
      );
      if (candidates.length === 0) {
        throw new MapGenError(`No free orbit in zone ${zone ?? 'any'} for ${player.faction}`);
      }
      // Middle of the zone: the most typical orbit for that kind of world.
      orbit = candidates[Math.floor((candidates.length - 1) / 2)] as Orbit;
      orbitFor.set(player.faction, orbit);
      usedOrbits.add(orbit.index);
    }
    result.push({ player, orbit, angle });
  }
  return result;
}

function tryGenerate(options: GenerateOptions, rng: Rng, attempt: number): StarSystem | null {
  const starClass = options.starClass ?? 'G';
  const star = STAR_CLASSES[starClass];
  const mapRadius = BALANCE.mapRadiusByPlayers[options.players.length as PlayerCount];
  const orbits = buildOrbits(starClass, mapRadius);

  const starHexes = hexSpiral(ORIGIN, star.radiusHexes - 1);
  const coronaHexes = hexRing(ORIGIN, star.radiusHexes);
  const blocked = new Set<string>([...starHexes, ...coronaHexes].map(hexKey));
  const inMap = (h: Hex) => hexDistance(ORIGIN, h) <= mapRadius;

  // Capitals.
  const planets: Planet[] = [];
  for (const slot of placeHomes(options.players, orbits, rng)) {
    const h = hexOnRingAt(slot.orbit.ringRadius, slot.angle);
    if (blocked.has(hexKey(h))) return null;
    planets.push({
      id: `home-${slot.player.id}`,
      orbit: slot.orbit.index,
      hex: h,
      kind: planetKindFor(slot.orbit.zone, true, rng),
      homeOf: slot.player.id,
    });
    blocked.add(hexKey(h));
  }
  const capitals = planets.map((p) => p.hex);

  // Fairness: capitals far enough apart.
  for (let i = 0; i < capitals.length; i++) {
    for (let j = i + 1; j < capitals.length; j++) {
      if (hexDistance(capitals[i] as Hex, capitals[j] as Hex) < BALANCE.minCapitalDistance)
        return null;
    }
  }

  // Fair surroundings (#98): every capital gets the same bodies within its reach, on
  // hexes nearer to it than to any other capital, kept apart from each other.
  const rich = RICHNESS[options.richness ?? 'balanced'];
  const ringOf = (h: Hex) => hexDistance(ORIGIN, h);
  const orbitAt = new Map(orbits.map((o) => [o.ringRadius, o]));
  /** The capital a hex belongs to: strictly the nearest one, within reach; else −1. */
  const regionOf = (h: Hex): number => {
    let best = -1;
    let bestD = Infinity;
    let tie = false;
    capitals.forEach((c, i) => {
      const d = hexDistance(c, h);
      if (d < bestD) [best, bestD, tie] = [i, d, false];
      else if (d === bestD) tie = true;
    });
    return !tie && bestD <= rich.reach ? best : -1;
  };
  const spaced = (h: Hex, asteroid: boolean) =>
    planets.every((p) => {
      const d = hexDistance(p.hex, h);
      if (asteroid && p.kind === 'asteroids') return d >= MAP_SPACING.asteroids;
      if (asteroid || p.kind === 'asteroids') return d >= MAP_SPACING.asteroidToBody;
      return d >= MAP_SPACING.bodies;
    });
  const zonesFor = {
    gasGiants: new Set<ZoneId>(['giants']),
    asteroids: new Set<ZoneId>(['belt']),
    planets: new Set<ZoneId>(['hot', 'habitable', 'outer']),
  } as const;
  const everyHex = hexSpiral(ORIGIN, mapRadius);
  for (const kind of ['gasGiants', 'planets', 'asteroids'] as const) {
    for (let i = 0; i < capitals.length; i++) {
      const spots = rng.shuffle(
        everyHex.filter((h) => {
          const orbit = orbitAt.get(ringOf(h));
          return (
            orbit !== undefined &&
            zonesFor[kind].has(orbit.zone) &&
            !blocked.has(hexKey(h)) &&
            regionOf(h) === i
          );
        }),
      );
      for (let n = 0; n < rich.perCapital[kind]; n++) {
        const spot = spots.find((h) => !blocked.has(hexKey(h)) && spaced(h, kind === 'asteroids'));
        if (!spot) return null;
        const orbit = orbitAt.get(ringOf(spot))!;
        planets.push({
          id: `planet-${planets.length}`,
          orbit: orbit.index,
          hex: spot,
          kind: planetKindFor(orbit.zone, false, rng),
          homeOf: null,
        });
        blocked.add(hexKey(spot));
      }
    }
  }

  // Lagrange points: 60° ahead (L4) and behind (L5) every gas giant on its orbit.
  const lagrangePoints: LagrangePoint[] = [];
  for (const giant of planets.filter((p) => p.kind === 'gasGiant')) {
    const orbit = orbits[giant.orbit]!;
    const angle = hexAngle(giant.hex);
    for (const [side, delta] of [
      ['L4', Math.PI / 3],
      ['L5', -Math.PI / 3],
    ] as const) {
      const h = hexOnRingAt(orbit.ringRadius, angle + delta);
      if (blocked.has(hexKey(h)) || !inMap(h)) continue;
      lagrangePoints.push({ id: `${giant.id}-${side}`, hex: h, giantId: giant.id, side });
      blocked.add(hexKey(h));
    }
  }
  // Fairness: no capital much farther from its nearest Lagrange point than the others.
  if (lagrangePoints.length > 0) {
    const nearest = capitals.map((c) =>
      Math.min(...lagrangePoints.map((l) => hexDistance(c, l.hex))),
    );
    if (Math.max(...nearest) - Math.min(...nearest) > BALANCE.lagrangeFairnessSpread) return null;
  }

  // Dust clouds keep a one-hex gap from planets, Lagrange points and each other.
  const reserved = new Set(blocked);
  for (const p of planets) for (const h of hexRing(p.hex, 1)) reserved.add(hexKey(h));
  for (const l of lagrangePoints) for (const h of hexRing(l.hex, 1)) reserved.add(hexKey(h));

  const clouds: DustCloud[] = [];
  const placeCloud = (center: Hex, level: CloudLevel): boolean => {
    const hexes = hexSpiral(center, CLOUD_RADIUS[level]);
    if (!hexes.every((h) => inMap(h) && !reserved.has(hexKey(h)))) return false;
    clouds.push({ id: `cloud-${clouds.length}`, level, center, hexes });
    for (const h of hexSpiral(center, CLOUD_RADIUS[level] + 1)) reserved.add(hexKey(h));
    return true;
  };

  // Guaranteed starting pocket around every capital, then the rest of its clouds
  // within reach (#98), bigger ones first so they still find room.
  for (let i = 0; i < capitals.length; i++) {
    const capital = capitals[i]!;
    const candidates = rng.shuffle(
      hexSpiral(capital, BALANCE.startPocketRadius).filter(
        (h) => hexDistance(capital, h) >= 2 && regionOf(h) === i,
      ),
    );
    let placed = 0;
    for (const h of candidates) {
      if (placed === BALANCE.startPocketClouds) break;
      if (placeCloud(h, 1)) placed++;
    }
    if (placed < BALANCE.startPocketClouds) return null;
  }
  for (const level of [3, 2, 1] as const) {
    for (let i = 0; i < capitals.length; i++) {
      // A cloud counts as the capital's when all of it lies in the capital's region.
      const wanted = rich.perCapital.clouds[level] - (level === 1 ? BALANCE.startPocketClouds : 0);
      let placed = 0;
      for (const h of rng.shuffle(everyHex.filter((x) => regionOf(x) === i))) {
        if (placed >= wanted) break;
        if (!hexSpiral(h, CLOUD_RADIUS[level]).every((x) => regionOf(x) === i)) continue;
        if (placeCloud(h, level)) placed++;
      }
      if (placed < wanted) return null;
    }
  }
  // The contested middle: beyond every capital's reach, where it fits.
  for (const level of [3, 2, 1] as const) {
    let placed = 0;
    for (const h of rng.shuffle(everyHex.filter((x) => regionOf(x) === -1))) {
      if (placed === rich.middle.clouds[level] * capitals.length) break;
      if (!hexSpiral(h, CLOUD_RADIUS[level]).every((x) => regionOf(x) === -1)) continue;
      if (placeCloud(h, level)) placed++;
    }
  }

  return {
    seed: options.seed,
    starClass,
    mapRadius,
    starHexes,
    coronaHexes,
    orbits,
    planets,
    clouds,
    lagrangePoints,
    attempt,
  };
}

/**
 * Deterministic: the same seed and players always give the same system.
 * Retries with independent random streams until the fairness checks pass.
 */
export function generateSystem(options: GenerateOptions): StarSystem {
  validatePlayers(options.players);
  const root = Rng.fromSeed(`system:${options.seed}`);
  for (let attempt = 0; attempt < BALANCE.mapGenAttempts; attempt++) {
    const system = tryGenerate(options, root.fork(`attempt-${attempt}`), attempt);
    if (system) return system;
  }
  throw new MapGenError(
    `No fair map after ${BALANCE.mapGenAttempts} attempts (seed ${options.seed})`,
  );
}

/** Level-1 clouds within the starting pocket of a capital (used by checks and reports). */
export function pocketClouds(system: StarSystem, capital: Hex): DustCloud[] {
  return system.clouds.filter(
    (c) => c.level === 1 && hexDistance(c.center, capital) <= BALANCE.startPocketRadius,
  );
}
